import type { Person, PersonId } from '../types.js';
import { nameSimilarity, words } from '../text.js';
import type { PersonSet, SplitContext, SplitOp } from './dsl.js';

/**
 * What the model returns for a sentence: the people it introduced, whether
 * the sentence spoke about the whole group, a stated head count, and the ops.
 *
 * Letting the model *declare* people is what removes the roster screen. It
 * also reopens the hallucination door the id-only DSL had closed, so this
 * module is the new guard: a declared name must be findable in the sentence
 * the user actually said, or the whole reply is rejected.
 */
export interface DeclaredPerson {
  /** Model-chosen id, e.g. "n1". Only meaningful inside this reply. */
  id: string;
  name: string;
}

export interface SplitReply {
  people: DeclaredPerson[];
  /** "we were five", "the four of us". Absent when the sentence did not say. */
  groupSize?: number;
  /** The sentence used "everyone", "we all", "the rest of us"… */
  everyone: boolean;
  ops: SplitOp[];
}

export interface ApplyOptions {
  /** The user's answer to "how many were you?", which wins over the sentence. */
  headCount?: number;
  /** Renames the user made afterwards, keyed by final person id. */
  renames?: Record<PersonId, string>;
}

export interface ApplyResult {
  /** The final roster: existing people, then introduced ones, then guests. */
  people: Person[];
  /** The speaker gave their own name in the sentence: "Tudor (me)". */
  speakerAlias?: string;
  /** The ops with declared ids rewritten to roster ids. */
  ops: SplitOp[];
  /** Deterministic reasons the reply is unusable. Empty means proceed. */
  errors: string[];
  /**
   * The sentence spoke about "everyone" but never said how many that is, and
   * the user has not answered yet. The UI asks one question.
   */
  askHeadCount: boolean;
}

export const GUEST_PREFIX = 'Guest';

const SELF_WORDS = new Set(['me', 'i', 'myself', 'moi', 'eu', 'yo', 'ich', 'io']);

/**
 * Words that describe a group, not a person. The model sometimes declares
 * "Friends" as if it were someone's name; it means "the people not named".
 */
const GROUP_WORDS = new Set([
  'friend', 'friends', 'others', 'other', 'rest', 'guys', 'people', 'everyone', 'everybody',
  'colleagues', 'family', 'kids', 'children', 'girls', 'boys', 'guests', 'mates', 'buddies',
  'prieteni', 'ceilalti', 'ceilalți', 'restul', 'amigos', 'freunde', 'amis',
]);

const COUNT_WORDS: Record<string, number> = {
  a: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8,
  couple: 2, few: 3, doi: 2, două: 2, trei: 3, patru: 4, cinci: 5, dos: 2, tres: 3, cuatro: 4,
};

/**
 * "2 more friends", "three others", "a couple of friends", "two other guys":
 * people the sentence counts without naming. Returns how many.
 */
export function unnamedCountIn(utterance: string): number | undefined {
  const pattern =
    /\b(\d{1,2}|a|one|two|three|four|five|six|seven|eight|couple|few|doi|două|trei|patru|cinci|dos|tres|cuatro)\s+(?:of\s+)?(?:more|other|extra|alți|alte|otros|otras)?\s*(friends?|others?|guys|people|mates|buddies|colleagues|kids|girls|boys|guests|prieteni|amigos|persoane)\b/iu;
  const match = pattern.exec(utterance);
  if (!match?.[1]) return undefined;
  const raw = match[1].toLowerCase();
  const n = /^\d+$/.test(raw) ? Number.parseInt(raw, 10) : COUNT_WORDS[raw];
  return n && n > 0 ? n : undefined;
}

const ORDINALS: Record<string, number> = { one: 1, first: 1, two: 2, second: 2, three: 3, third: 3, four: 4, fourth: 4, five: 5, fifth: 5 };

/** "G1", "guest 2", "guest one", "the first guest" → the guest's number. */
export function guestNumberIn(name: string): number | undefined {
  const text = name.trim().toLowerCase();
  const short = /^g\s*(\d{1,2})$/.exec(text);
  if (short?.[1]) return Number.parseInt(short[1], 10);
  const long = /^(?:the\s+)?(?:guest\s*(\d{1,2}|one|two|three|four|five)|(first|second|third|fourth|fifth)\s+guest)$/.exec(text);
  if (!long) return undefined;
  const token = long[1] ?? long[2] ?? '';
  return /^\d+$/.test(token) ? Number.parseInt(token, 10) : ORDINALS[token];
}

/** "Tudor (me)", "me (Tudor)", "Tudor, that's me", "I'm Tudor". */
const ALIAS_PATTERNS = [
  /\b([\p{L}][\p{L}'-]+)\s*\(\s*(?:me|i|myself|eu)\s*\)/iu,
  /\b(?:me|i|myself|eu)\s*\(\s*([\p{L}][\p{L}'-]+)\s*\)/iu,
  /\b([\p{L}][\p{L}'-]+)\s*,?\s*that'?s me\b/iu,
  /\b(?:i'?m|i am|my name is|eu sunt|sunt)\s+([\p{L}][\p{L}'-]+)/iu,
];

/** The name the speaker gave themselves in the sentence, if any. */
export function speakerAliasIn(utterance: string): string | undefined {
  for (const pattern of ALIAS_PATTERNS) {
    const match = pattern.exec(utterance);
    const name = match?.[1];
    if (name && !SELF_WORDS.has(name.toLowerCase())) {
      return name[0]?.toUpperCase() + name.slice(1);
    }
  }
  return undefined;
}

/** A declared name counts as present if some word of the sentence is close to it. */
export function nameInSentence(name: string, utterance: string): boolean {
  const tokens = words(utterance);
  const target = words(name);
  if (target.length === 0) return false;
  // Multi-word names ("Ana Maria") match if each word matches somewhere.
  return target.every((part) => tokens.some((token) => nameSimilarity(part, token) >= 0.75));
}

/** True when the name is (a word of) an item on the bill. */
export function looksLikeAnItem(name: string, context: SplitContext): boolean {
  const parts = words(name);
  if (parts.length === 0) return false;
  return context.items.some((item) => {
    const itemWords = words(item.name);
    return (
      nameSimilarity(item.name, name) >= 0.8 ||
      parts.every((part) => itemWords.some((word) => word.length > 2 && nameSimilarity(part, word) >= 0.85))
    );
  });
}

export function nextPersonId(people: Person[]): PersonId {
  const taken = new Set(people.map((person) => person.id));
  let n = people.length + 1;
  while (taken.has(`p${n}`)) n += 1;
  return `p${n}`;
}

/**
 * Rewrite a person set from declared ids to roster ids. A declared *group*
 * ("Friends") means the people not named, so `only [friends]` becomes
 * `allExcept [everyone named]` and `allExcept [friends]` becomes `only [named]`.
 */
function rewriteSet(set: PersonSet, map: Map<string, PersonId>, groups: Set<string>, named: PersonId[]): PersonSet {
  const hasGroup = set.ids.some((id) => groups.has(id));
  const ids = set.ids.filter((id) => !groups.has(id)).map((id) => map.get(id) ?? id);
  if (!hasGroup) return { ...set, ids };
  if (set.kind === 'only') {
    // "Friends and Alex had the salad": the unnamed plus Alex = everyone except the other named.
    const keep = new Set(ids);
    return { kind: 'allExcept', ids: named.filter((id) => !keep.has(id)) };
  }
  return { kind: 'only', ids: named.filter((id) => !ids.includes(id)) };
}

/**
 * Turn a reply into a concrete roster and ops that reference it.
 *
 * Order of precedence for who a declared name is:
 *   1. a self word ("me") → the speaker;
 *   2. someone already on the roster with a close-enough name → that person;
 *   3. a name that appears in the sentence → a new person;
 *   4. anything else → an error. The model named someone the user never said.
 */
export function applyReply(
  reply: SplitReply,
  context: SplitContext,
  utterance: string,
  options: ApplyOptions = {},
): ApplyResult {
  const errors: string[] = [];
  const people: Person[] = context.people.map((person) => ({ ...person }));
  const speaker =
    people.find((person) => person.id === context.speakerId) ?? people[0];
  const map = new Map<string, PersonId>();
  const groups = new Set<string>();
  const speakerAlias = speakerAliasIn(utterance);

  for (const declared of reply.people) {
    const name = declared.name.trim();
    if (name === '') {
      errors.push(`declared person "${declared.id}" has no name`);
      continue;
    }
    if (words(name).every((word) => GROUP_WORDS.has(word))) {
      groups.add(declared.id);
      continue;
    }
    if (SELF_WORDS.has(name.toLowerCase()) || (speakerAlias && nameSimilarity(name, speakerAlias) >= 0.8)) {
      if (speaker) map.set(declared.id, speaker.id);
      else errors.push('the sentence says "me" but nobody is the speaker');
      continue;
    }
    const existing = people.find((person) => nameSimilarity(person.name, name) >= 0.8);
    if (existing) {
      map.set(declared.id, existing.id);
      continue;
    }
    // "g1", "guest one", "the first guest" → Guest 1, if that guest exists.
    const guest = guestNumberIn(name);
    if (guest !== undefined) {
      const found = people.find((person) => person.name === `${GUEST_PREFIX} ${guest}`);
      if (found) {
        map.set(declared.id, found.id);
        continue;
      }
    }
    if (!nameInSentence(name, utterance)) {
      errors.push(`"${name}" is not in the sentence`);
      continue;
    }
    // A dish is not a person. "Coca-Cola" appears in the sentence too.
    if (looksLikeAnItem(name, context)) {
      errors.push(`"${name}" is something on the bill, not a person`);
      continue;
    }
    const person: Person = { id: nextPersonId(people), name };
    people.push(person);
    map.set(declared.id, person.id);
  }

  const named = people.map((person) => person.id);
  const ops: SplitOp[] = reply.ops.map((op) =>
    op.kind === 'assign'
      ? { ...op, to: rewriteSet(op.to, map, groups, named) }
      : { ...op, from: rewriteSet(op.from, map, groups, named) },
  );

  // "2 more friends" is a head count in disguise: the named people plus two.
  // The engine's count wins over the model's when the sentence is explicit:
  // "me, Carla, Alex and 2 more" is five however the model added it up.
  const unnamed = unnamedCountIn(utterance);
  const impliedSize = unnamed !== undefined ? people.length + unnamed : undefined;
  const stated =
    impliedSize !== undefined ? Math.max(reply.groupSize ?? 0, impliedSize) : reply.groupSize;
  const size = options.headCount ?? stated;
  if (size !== undefined && Number.isInteger(size) && size > people.length) {
    let guest = 1;
    while (people.length < size) {
      const name = `${GUEST_PREFIX} ${guest}`;
      guest += 1;
      if (people.some((person) => person.name === name)) continue;
      people.push({ id: nextPersonId(people), name });
    }
  }

  const renames = options.renames ?? {};
  for (const person of people) {
    const renamed = renames[person.id];
    if (renamed && renamed.trim() !== '') person.name = renamed.trim();
  }

  const askHeadCount =
    (reply.everyone || groups.size > 0) &&
    options.headCount === undefined &&
    reply.groupSize === undefined &&
    impliedSize === undefined;

  return { people, ops, errors, askHeadCount, ...(speakerAlias ? { speakerAlias } : {}) };
}
